import type { StockMovement } from '@/types'

/**
 * Plain-language translation of a stock-ledger row (F-FE-N104).
 *
 * The raw feed spoke in movement_type + "[Tag] note" strings; the reader had
 * to know that "Adjustment" might mean shrinkage, that "[Return to Vendor]"
 * is money coming back, and that an opening_balance is an asserted quantity,
 * not a purchase. This maps every row (and its links: order, PO, count,
 * supplier) to a title + tone. Exhaustive over the 7 written movement types —
 * keep in step with the backend's describeMovementLabel (stock_history.go).
 */

export type MovementTone = 'neutral' | 'positive' | 'caution' | 'muted'

export interface MovementDescription {
  title: string
  badge: string
  tone: MovementTone
  /** Free-text remainder of the note after the [Tag] prefix, if any. */
  detail?: string
}

/** Splits "[Reason] free text" into its parts. */
export function splitNote(note: string | null | undefined): { reason?: string; rest?: string } {
  const t = (note ?? '').trim()
  if (!t.startsWith('[')) return { rest: t || undefined }
  const end = t.indexOf(']')
  if (end <= 1) return { rest: t || undefined }
  const reason = t.slice(1, end)
  const rest = t.slice(end + 1).replace(/^[\s—-]+/, '').trim()
  return { reason, rest: rest || undefined }
}

export function describeMovement(m: Pick<StockMovement,
  'movement_type' | 'note' | 'order_number' | 'purchase_order_id' | 'stock_count_id' | 'supplier_name' | 'issued_to_name' | 'line_count'>): MovementDescription {
  const { reason, rest } = splitNote(m.note)
  const orderRef = m.order_number ? ` · Order #${m.order_number}` : ''

  switch (m.movement_type) {
    case 'sale': {
      const items = (m.line_count ?? 1) > 1 ? ` · ${m.line_count} items` : ''
      return { title: `Sold${orderRef}${items}`, badge: 'Sale', tone: 'neutral' }
    }
    case 'sale_reversal':
      return { title: `Returned to stock${orderRef}`, badge: 'Refund', tone: 'neutral', detail: rest }
    case 'purchase':
      if (m.purchase_order_id) {
        return { title: 'Received (purchase order)', badge: 'Received', tone: 'positive', detail: rest }
      }
      return {
        title: m.supplier_name ? `Purchase — ${m.supplier_name}` : 'Purchase recorded',
        badge: 'Received', tone: 'positive', detail: rest,
      }
    case 'adjustment':
      if (m.stock_count_id) {
        return { title: 'Count correction', badge: 'Count', tone: 'neutral', detail: rest }
      }
      if (reason === 'Damage / loss') {
        return { title: 'Written off — damage / loss', badge: 'Write-off', tone: 'caution', detail: rest }
      }
      return { title: reason ? `Adjusted — ${reason}` : 'Stock adjusted', badge: 'Adjustment', tone: 'neutral', detail: rest }
    case 'issue':
      if (reason === 'Spoilage/Waste') {
        return { title: 'Wasted — spoilage', badge: 'Waste', tone: 'caution', detail: rest }
      }
      if (reason === 'Return to Vendor') {
        // Money usually comes back — never alarm-red.
        return { title: 'Returned to vendor', badge: 'Vendor return', tone: 'neutral', detail: rest }
      }
      return {
        title: m.issued_to_name
          ? `Issued to ${m.issued_to_name}${reason ? ` — ${reason}` : ''}`
          : reason ? `Used — ${reason}` : 'Issued',
        badge: 'Issued', tone: 'neutral', detail: rest,
      }
    case 'supplier_return':
      return {
        title: m.supplier_name ? `Returned to ${m.supplier_name}` : 'Returned to vendor',
        badge: 'Vendor return', tone: 'neutral', detail: rest,
      }
    case 'opening_balance':
      if ((m.note ?? '').startsWith('[Opening Balance Backfill]')) {
        return { title: 'Opening balance (system reconciliation)', badge: 'Opening', tone: 'muted' }
      }
      return { title: 'Opening balance', badge: 'Opening', tone: 'muted', detail: rest }
    // Kitchen flow lane B: the two sides of a production batch. The note is
    // "Production PB-…: <prep item>", shown as the detail.
    case 'production_consume':
      return { title: 'Used in production', badge: 'Production', tone: 'neutral', detail: rest }
    case 'production_output':
      return { title: 'Made in the kitchen', badge: 'Made', tone: 'positive', detail: rest }
    // Kitchen flow (lane C): requisition transfers between locations. A pair
    // moves stock between shelves; only transfer_variance is a loss.
    case 'transfer_out':
      return { title: 'Sent on (requisition)', badge: 'Transfer', tone: 'muted', detail: rest || m.note || undefined }
    case 'transfer_in':
      return { title: 'Arrived (requisition)', badge: 'Transfer', tone: 'muted', detail: rest || m.note || undefined }
    case 'transfer_variance':
      return { title: 'Missing in transit — written off', badge: 'Write-off', tone: 'caution', detail: rest || m.note || undefined }
    // Go-live (migration 145): one item merged into another. The note says
    // "Merged into <A>" / "Merged from <B>".
    case 'merge_out':
      return { title: m.note || 'Merged into another item', badge: 'Merged', tone: 'muted' }
    case 'merge_in':
      return { title: m.note || 'Merged in', badge: 'Merged', tone: 'muted' }
    default: {
      // Exhaustive today; a type added by a later lane still renders.
      const t = String(m.movement_type)
      return { title: t.replace(/_/g, ' '), badge: t, tone: 'neutral', detail: rest }
    }
  }
}

export const MOVEMENT_TONE_CLS: Record<MovementTone, string> = {
  neutral: '',
  positive: 'text-success-fg',
  caution: 'text-warning-fg',
  muted: 'text-muted-foreground',
}
